import { useEffect, useRef } from "react";
import { change } from "redux-form";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import MapService from "services/map.service";
import PricingService from "services/pricing.service";
import ToasterService from "utils/toaster.util";
import { calculateZoneNetPrice } from "utils/pricingCalc.util";
import SpecialPriceService from "services/specialPrice.service";
import { toWallClockIso } from "utils/specialPrice.util";

// A picked location carries `details.coordinates`; one hydrated from a saved
// quotation may only carry `details.geometry.location`. Accept either.
const toLatLng = (place: any) => {
  const point =
    place?.details?.coordinates || place?.details?.geometry?.location;
  if (!point) return null;
  const lat = typeof point.lat === "function" ? point.lat() : point.lat;
  const lng = typeof point.lng === "function" ? point.lng() : point.lng;
  if (lat == null || lng == null) return null;
  return { lat: Number(lat), lng: Number(lng) };
};

const Calculations = ({ index, member }: { index: number; member: string }) => {
  let form = "AddQuotationForm";
  let zoneID = '64fb1c3537a2a1b4fa9b907c'
  const {
    hourly,
    class_id,
    duration,
    per_hour,
    quantity,
    base_price,
    net_price,
    tax,
    pickup,
    destination,
    start_date,
  } = useAppSelector((state) => state.form?.[form]?.values?.bookings[index]);

  // const pricing = useAppSelector((state) => state.newPricing.pricingsByZone);
  const pricing = useAppSelector((state) => state.newPricing.pricingsByZone[`${zoneID}_individual`]?.pricings);
  let price = (class_id && pricing?.find((item: any) => item.class_id?._id === class_id)) || null;

  // Special prices depend on this row's pickup datetime. The slice caches by
  // `${zone}|${iso}`, so rows sharing a datetime share one request.
  const pickupIso = toWallClockIso(start_date?.date);
  const specialPercentByClass = useAppSelector(
    (state) => state.specialPrice.applicable[`${zoneID}|${pickupIso}`]
  );
  const specialPercent = specialPercentByClass?.[class_id] || 0;

  const dispatch = useAppDispatch();
  const prevHourlyRef = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    if (!pickupIso) return;
    if (specialPercentByClass) return;
    SpecialPriceService.getApplicableSpecialPrices(zoneID, pickupIso);
  }, [zoneID, pickupIso, specialPercentByClass]);



  // Toggling Hourly switches which inputs drive the price. Drop the fields that
  // belong to the mode we just left, otherwise their stale values keep feeding
  // the net/gross effects below (e.g. an old `per_hour` still pricing a trip
  // booking after Hourly is unchecked).
  useEffect(() => {
    const wasHourly = prevHourlyRef.current;
    prevHourlyRef.current = hourly;
    if (wasHourly === undefined || wasHourly === hourly) return;

    if (hourly) {
      dispatch(change(form, `${member}.base_price`, ""));
      dispatch(change(form, `${member}.distance`, ""));
    } else {
      dispatch(change(form, `${member}.per_hour`, ""));
      dispatch(change(form, `${member}.duration`, ""));
    }
    dispatch(change(form, `${member}.net_price`, ""));
    dispatch(change(form, `${member}.gross_price`, ""));
  }, [hourly, member, dispatch]);

  // Tax always comes from the priced zone (Add Booking uses `zone.tax`).
  useEffect(() => {
    if (!price) return;
    dispatch(change(form, `${member}.tax`, price?.zone_id?.tax ?? 15));
  }, [price, member, dispatch]);

  // Default the hourly rate from the class pricing.
  useEffect(() => {
    if (!hourly || !price) return;
    dispatch(change(form, `${member}.per_hour`, price.per_hour_rate));
  }, [hourly, price, member, dispatch]);

  // Net price — hourly mode.
  useEffect(() => {
    if (!hourly) return;
    if (!per_hour) return;
    let net = Number(per_hour);
    if (duration) net = net * Number(duration);
    // Applied here as well as in the trip path below: this screen builds the
    // hourly net by hand rather than through calculateZoneNetPrice, and
    // yelo-userback increases hourly prices too.
    if (specialPercent) net = net * (1 + Number(specialPercent) / 100);
    if (quantity) net = net * Number(quantity);
    dispatch(change(form, `${member}.net_price`, net.toFixed(2)));
  }, [hourly, per_hour, duration, quantity, specialPercent, member, dispatch]);

  // Net price — trip mode. `base_price` is the route price from the effect below
  // (admin-editable); quantity scales it.
  useEffect(() => {
    if (hourly) return;
    if (!base_price) {
      dispatch(change(form, `${member}.net_price`, ""));
      return;
    }
    let net = Number(base_price);
    if (quantity) net = net * Number(quantity);
    dispatch(change(form, `${member}.net_price`, net.toFixed(2)));
  }, [hourly, base_price, quantity, member, dispatch]);

  // Single place gross is derived, so it can never lag net_price or tax.
  useEffect(() => {
    if (!net_price) {
      dispatch(change(form, `${member}.gross_price`, ""));
      return;
    }
    const gross = Number(net_price) * (1 + Number(tax || 0) / 100);
    dispatch(change(form, `${member}.gross_price`, gross.toFixed(2)));
  }, [net_price, tax, member, dispatch]);


  useEffect(() => {
    const calculateDistance = async () => {
      if (
        pickup?.address &&
        destination?.address &&
        pickup?.address === destination?.address
      ) {
        return ToasterService.showError(
          "Pickup and Destination address must be different!"
        );
      }
      if (pickup?.details?.place_id && destination?.details?.place_id) {
        if (!price) return;

        let coordinates: any[] = [];
        let destinationCoordinates: any[] = [];

        const { location } = pickup?.details?.geometry;
        coordinates = [[location.lng, location.lat]];
        let pickupCoordinates = [location.lat, location.lng];
        if (destination) {
          const { location } = destination.details?.geometry;
          coordinates.push([location.lng, location.lat]);
          destinationCoordinates = [location.lat, location.lng];
        }
        const res: any = await MapService.calculateDistance(
          pickup.details.formatted_address || pickup.details.address,
          destination.details.formatted_address || destination.details.address,
          coordinates,
          pickupCoordinates,
          destinationCoordinates
        );
        if (!res || res.distance == null) return;

        // Same rounding as Add Booking, which stores distance at 2 decimals
        // before feeding it to the km-range calculation.
        const distance = Number(res.distance).toFixed(2);

        // Shared with Add Booking so both screens price a route identically.
        let netto = calculateZoneNetPrice({
          pricing: price,
          distance,
          hourly: false,
          airport: !!pickup?.details?.airport,
          specialPercent,
        });

        // Cross-country (SA <-> BH) surcharge, as in Add Booking. Add Booking
        // applies it to the gross price; adding the net amount here is
        // equivalent because gross is derived as net * (1 + tax / 100).
        // Skipped rather than sent half-empty: the endpoint rejects missing
        // coordinates, which surfaces as an error toast on the form.
        const pickupLatLng = toLatLng(pickup);
        const destinationLatLng = toLatLng(destination);
        if (pickupLatLng && destinationLatLng) {
          const [crossCountrySuccess]: any =
            await PricingService.getPricingCrossCountry(
              pickupLatLng,
              destinationLatLng
            );
          const crossCountry = crossCountrySuccess?.data?.data?.crossCountry;
          if (crossCountry?.price) netto += Number(crossCountry.price);
        }

        dispatch(change(form, `${member}.distance`, distance));
        dispatch(change(form, `${member}.base_price`, netto.toFixed(2)));

      }
    };
    if (
      class_id &&
      pickup?.details?.place_id &&
      destination?.details?.place_id &&
      !hourly
    ) {
      calculateDistance();
    }
  }, [pickup, destination, dispatch, class_id, price, hourly, specialPercent]);
  // }, [pickup, destination, dispatch, class_id, price, hourly, quantity]);


  return null;
};

export default Calculations;
